import React, { useEffect, useState, useRef } from 'react';
import { Send, User } from 'lucide-react';
import { MondrianButton } from './ui/MondrianButton';
import { MondrianPanel } from './ui/MondrianPanel';
import { motion } from 'framer-motion';
interface Message {
  id: string;
  sender: 'user' | 'mondrian';
  text: string;
}
const INITIAL_MESSAGES: Message[] = [{
  id: '1',
  sender: 'mondrian',
  text: '你好。我是皮特·蒙德里安。我看到你试图剥离表象，寻找本质。这很好。'
}];
const MONDRIAN_RESPONSES = ['斜线破坏了宇宙的宁静与平衡，只有水平和垂直线才是永恒的。', '红黄蓝是色彩的本质，其他颜色都是杂质。我要剥去自然的表象。', '艺术的目的是揭示宇宙的和谐，而非模仿自然的外表。', '爵士乐的切分音节奏与我的绘画有着相同的精神——打破传统，寻找新的平衡。', '自然是可恨的，因为它充满了无序和偶然。我们要建立纯粹的关系。', '杜斯伯格那个叛徒，竟然在画里引入了斜线！那是对新造型主义的背叛！', '你看这幅画，它还不够纯粹。太多的情感，太少的理性。', '我们要消除悲剧性。悲剧源于个体的分离，而新造型主义追求普遍的统一。'];
export function DigitalSpirit() {
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);
  const handleSend = () => {
    if (!input.trim()) return;
    const userMsg: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: input
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    // Simulate AI response
    setTimeout(() => {
      const randomResponse = MONDRIAN_RESPONSES[Math.floor(Math.random() * MONDRIAN_RESPONSES.length)];
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'mondrian',
        text: randomResponse
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1500);
  };
  return <div className="space-y-8" data-id="element-97">
      <div className="text-center space-y-2" data-id="element-98">
        <h2 className="text-2xl font-bold uppercase" data-id="element-99">
          工具三：蒙德里安的数字灵魂
        </h2>
        <p className="text-gray-600" data-id="element-100">与大师对话，聆听新造型主义的哲学教诲</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 h-[600px]" data-id="element-101">
        {/* Avatar / Profile */}
        <div className="md:col-span-1" data-id="element-102">
          <MondrianPanel className="h-full flex flex-col items-center text-center gap-6" data-id="element-103">
            {/* Geometric Avatar */}
            <div className="w-32 h-40 bg-mondrian-white border-4 border-mondrian-black relative" data-id="element-104">
              <div className="absolute top-4 left-4 right-4 h-8 bg-mondrian-black" data-id="element-105"></div>{' '}
              {/* Glasses */}
              <div className="absolute top-16 left-10 right-10 h-12 bg-mondrian-white border-2 border-mondrian-black" data-id="element-106"></div>{' '}
              {/* Face */}
              <div className="absolute bottom-0 left-0 right-0 h-10 bg-mondrian-blue" data-id="element-107"></div>{' '}
              {/* Suit */}
            </div>

            <div data-id="element-108">
              <h3 className="text-xl font-bold" data-id="element-109">Piet Mondrian</h3>
              <p className="text-sm text-gray-500 italic" data-id="element-110">1872 - 1944</p>
            </div>

            <div className="text-left text-sm space-y-2 p-4 bg-gray-50 border-2 border-mondrian-black w-full" data-id="element-111">
              <p data-id="element-112">
                <strong data-id="element-113">性格：</strong> 严肃、执着
              </p>
              <p data-id="element-114">
                <strong data-id="element-115">痛恨：</strong> 斜线、曲线、绿色
              </p>
              <p data-id="element-116">
                <strong data-id="element-117">热爱：</strong> 爵士乐、直线、三原色
              </p>
            </div>
          </MondrianPanel>
        </div>

        {/* Chat Interface */}
        <div className="md:col-span-2 flex flex-col h-full" data-id="element-118">
          <MondrianPanel className="flex-1 flex flex-col overflow-hidden" noPadding data-id="element-119">
            {/* Messages Area */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50" data-id="element-120">
              {messages.map(msg => <motion.div key={msg.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-121">
                  <div className={`
                      max-w-[80%] p-4 border-2 border-mondrian-black
                      ${msg.sender === 'user' ? 'bg-mondrian-blue text-white mr-2' : 'bg-mondrian-white text-black ml-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'}
                    `} data-id="element-122">
                    <p className="font-bold text-sm mb-1 opacity-80" data-id="element-123">
                      {msg.sender === 'user' ? 'YOU' : 'MONDRIAN'}
                    </p>
                    <p data-id="element-124">{msg.text}</p>
                  </div>
                </motion.div>)}
              {isTyping && <div className="flex justify-start" data-id="element-125">
                  <div className="bg-mondrian-white border-2 border-mondrian-black p-4 ml-2 flex gap-1" data-id="element-126">
                    <span className="w-2 h-2 bg-mondrian-black animate-bounce" data-id="element-127"></span>
                    <span className="w-2 h-2 bg-mondrian-black animate-bounce delay-100" data-id="element-128"></span>
                    <span className="w-2 h-2 bg-mondrian-black animate-bounce delay-200" data-id="element-129"></span>
                  </div>
                </div>}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t-4 border-mondrian-black bg-white flex gap-2" data-id="element-130">
              <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} placeholder="向蒙德里安提问..." className="flex-1 border-2 border-mondrian-black p-3 focus:outline-none focus:ring-2 focus:ring-mondrian-yellow font-bold" data-id="element-131" />
              <MondrianButton variant="red" onClick={handleSend} data-id="element-132">
                <Send className="w-5 h-5" data-id="element-133" />
              </MondrianButton>
            </div>
          </MondrianPanel>
        </div>
      </div>
    </div>;
}